import { useQueries } from '@tanstack/react-query'
import { Eye, EyeOff } from 'lucide-react'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { getTMinus1 } from '@fortem-monorepo/js-utilities'
import { useResizableState } from '@fortem-monorepo/react-hooks'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'

import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import { AllMappingsTable } from '@/pages/operations/mappings/components/AllMappingsTable'
import { AllSWAPAGGTable } from '@/pages/operations/mappings/components/AllSWAPAGGTable'
import { MissingMappingsTable } from '@/pages/operations/mappings/components/MissingMappingsTable'
import { SWAPAGGTable } from '@/pages/operations/mappings/components/SWAPAGGTable'
import { useMissingExternalMappings } from '@/react-query/queries/mappings'

export interface PanelToggleProps extends React.ComponentProps<'button'> {
  open: boolean
}

export const PanelToggle: React.FC<PanelToggleProps> = ({ open, children, ...props }) => {
  return (
    <Button
      size="sm"
      variant="ghost"
      className={'px-3! font-medium rounded-none border-none'}
      {...props}
    >
      {children}
      {open ? <Eye className={'text-green-700'} /> : <EyeOff className={'text-gray-400'} />}
    </Button>
  )
}

export interface MappingsPageProps {}

export const MappingsPage: React.FC<MappingsPageProps> = ({}) => {
  const api = useApi()

  const [mappingsState, swapaggState] = useResizableState(2)

  const missingExternalMappings = useMissingExternalMappings({
    value_date: toISO8601(getTMinus1()),
  })

  const [allMappings, swapagg, allSwapagg] = useQueries({
    queries: [
      {
        queryKey: ['mappings/mappings'],
        queryFn: () => {
          return api.swapagg.getMappings()
        },
      },
      {
        queryKey: ['mappings/swapagg-control'],
        queryFn: () => {
          return api.swapagg.getControl({ report_date: toISO8601(new Date()) })
        },
      },
      {
        queryKey: ['mappings/swapagg'],
        queryFn: () => {
          return api.swapagg.getSwapagg()
        },
      },
    ],
  })

  return (
    <Page metadata={{ title: 'Mappings' }} permissions={OPERATIONS.MAPPINGS.permissions}>
      <HeaderConfigBar noPadding>
        <div className={'flex items-center'}>
          <div className={'h-full w-px bg-border'} />
          <PanelToggle open={mappingsState.open} onClick={() => mappingsState.toggle()}>
            MAPPINGS
          </PanelToggle>
          <div className={'h-full w-px bg-border'} />
          <PanelToggle open={swapaggState.open} onClick={() => swapaggState.toggle()}>
            SWAPAGG
          </PanelToggle>
          <div className={'h-full w-px bg-border'} />
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'horizontal'} autoSaveId={'conditional'} className={'flex-1'}>
        {mappingsState.open && (
          <ResizablePanel defaultSize={50} minSize={10} id={`panel-mappings`} order={1}>
            <ResizablePanelGroup
              direction="vertical"
              autoSaveId={'conditional'}
              className={'flex min-h-full! h-auto! flex-1'}
            >
              <ResizablePanel
                defaultSize={50}
                minSize={10}
                id={`panel-mappings--missing`}
                order={1}
              >
                <MissingMappingsTable
                  query={missingExternalMappings}
                  rowData={missingExternalMappings.data}
                  domLayout={'normal'}
                />
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel defaultSize={50} minSize={10} id={`panel-mappings--all`} order={2}>
                <AllMappingsTable
                  query={allMappings}
                  rowData={allMappings.data}
                  domLayout={'normal'}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </ResizablePanel>
        )}
        {mappingsState.open && swapaggState.open && <ResizableHandle />}
        {swapaggState.open && (
          <ResizablePanel defaultSize={50} minSize={10} id={`panel-swapagg`} order={2}>
            <ResizablePanelGroup
              direction="vertical"
              autoSaveId={'conditional'}
              className={'flex min-h-full! h-auto! flex-1'}
            >
              <ResizablePanel
                defaultSize={50}
                minSize={10}
                id={`panel-swappag--mappings`}
                order={1}
              >
                <SWAPAGGTable query={swapagg} rowData={swapagg.data} domLayout={'normal'} />
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel defaultSize={50} minSize={10} id={`panel-swappag--all`} order={2}>
                <AllSWAPAGGTable
                  query={allSwapagg}
                  rowData={allSwapagg?.data as any[]}
                  domLayout={'normal'}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </ResizablePanel>
        )}
      </ResizablePanelGroup>
    </Page>
  )
}
